import { computed, unref, watch } from '@vue/composition-api';
import { createContext, useContext } from '@berlin-front/common/composables/context';
import { MemberRequestContext } from '@views/member/MemberRequestContext';
import { ROUTE_NAME } from '@/router/route-name';
import { ValueTypeOf } from '@berlin-front/common/utils';
import { useMutation } from 'vue-query';
import { BerlinBffContext } from '@services/BerlinBff';
import { ApiCacheContext } from '@services/ApiCacheContext';
import useFakeProgress from '@berlin-front/common/composables/useFakeProgress';
import { useRouter } from '@berlin-front/common/composables/useRouter';

type UseMemberConfirmInjectProps = {
  nextPage: ValueTypeOf<typeof ROUTE_NAME>;
  prevPage: ValueTypeOf<typeof ROUTE_NAME>;
};

/**
 * 会員情報確認のcomposable
 *
 * 画面から使用するcomposableには非同期処理を書かない。
 * @returns
 */
export const useMemberConfirmFactory = (injectProps: UseMemberConfirmInjectProps) => () => {
  const { registerMemberFormState, sexDivisionRender, prefectureName } = useContext(MemberRequestContext);

  const member = computed(() => ({
    name: [registerMemberFormState.familyName, registerMemberFormState.givenName].join(' '),
    kana: [registerMemberFormState.familyNameKana, registerMemberFormState.givenNameKana].join(' '),
    sex: sexDivisionRender(registerMemberFormState.sexDivision),
    birthday: [
      registerMemberFormState.birthdayYear,
      registerMemberFormState.birthdayMonth,
      registerMemberFormState.birthdayDay.padStart(2, '0'),
    ].join('-'),
    zip: registerMemberFormState.zip.replace(/(\d{3})(\d{4})/, '$1-$2'),
    address1: [
      unref(prefectureName),
      registerMemberFormState.address2First,
      registerMemberFormState.address2Second,
    ].join(''),
    address2: registerMemberFormState.address3,
    phoneNumber: registerMemberFormState.phoneNumber,
  }));

  const berlinBff = useContext(BerlinBffContext);

  const { atonePubKey, tokens } = useContext(ApiCacheContext);

  const { isIdle, mutate, data, error, isLoading } = useMutation('/atone/sms/notify-{ resend: false }', () =>
    berlinBff.member.atoneMemberControllerMemberSmsNotify(
      tokens.value.requestToken ?? '',
      {
        phoneNumber: registerMemberFormState.phoneNumber,
        mailAddress: registerMemberFormState.mailAddress,
        resend: false,
      },
      atonePubKey.value,
    ),
  );

  const router = useRouter();

  const progress = useFakeProgress(
    1000,
    isLoading,
    computed(() => !isLoading.value && !isIdle.value),
  );

  // プログレスの完了、SMSコード送信の完了を監視し、画面遷移する。
  watch([() => progress.completed, data, error] as const, () => {
    // プログレスバー進行中は処理を行わない。
    if (!progress.completed) return;
    if (data.value || error.value) {
      // エラーでもそうでなくても遷移する。
      void router.replace({ name: injectProps.nextPage });
    }
  });

  const disabled = computed(() => progress.isLoading);

  const onClickSendSmsCode = () => {
    if (isIdle.value) {
      mutate();
    }
  };

  const onClickBack = () => {
    if (isIdle.value) {
      void router.replace({ name: injectProps.prevPage });
    }
  };

  return {
    member,
    onClickSendSmsCode,
    onClickBack,
    disabled,
    progress,
  };
};

export const ConfirmMemberContext = createContext({
  useMemberConfirm: useMemberConfirmFactory({
    nextPage: ROUTE_NAME.ATONE_MEMBER_AUTH,
    prevPage: ROUTE_NAME.ATONE_MEMBER_REGISTER,
  }),
});
